/**
 * Enhanced Agent Management Service with Backend Integration
 *
 * This service extends the original agent service to add backend persistence
 * for agent management.
 */

import { toast } from 'react-toastify';
import * as agentApi from '../../api/agentApi';
import {
  sendConfigToAgent as mqttSendConfigToAgent,
  sendCommandToAgent as mqttSendCommandToAgent,
} from '../mqtt/mqttService';

// Real-time communication functions using mqttService
const sendCommandToAgent = async (agentId, commandData) => {
  const type = commandData?.type || commandData?.action || 'custom';
  const { type: _ignoredType, action: _ignoredAction, ...payload } = commandData || {};
  return mqttSendCommandToAgent(agentId, type, payload);
};

const sendConfigToAgent = async (agentId, features = {}, settings = {}) => {
  return mqttSendConfigToAgent(agentId, features, settings);
};

const subscribeToAgentStatus = (agentId, callback) => {
  console.log(`[MQTT] Would subscribe to agent ${agentId} status`);
  return () => console.log(`[MQTT] Would unsubscribe from agent ${agentId} status`);
};

const listenForAllAgents = callback => {
  console.log(`[MQTT] Would listen for all agents`);
  return () => console.log(`[MQTT] Would stop listening for all agents`);
};

// Local cache of known agents and their status
const agentCache = new Map();
let isBackendEnabled = true; // Toggle for migration phase

/**
 * Fetch all agents from backend with optional filtering/pagination
 * Falls back to local cache if backend fails
 */
export const fetchAgents = async (params = {}) => {
  try {
    if (isBackendEnabled) {
      console.log('Fetching agents from backend API with params:', params);
      // Get from backend
      const response = await agentApi.fetchAgents(params);

      // Handle v2 API response format
      let agents;
      if (response.agents) {
        // Legacy or custom format
        agents = response.agents;
      } else if (response.data && response.data.items) {
        // v2 API format
        agents = response.data.items;
      } else {
        // Fallback
        agents = response.data || response;
      }

      // Update local cache with backend data
      if (Array.isArray(agents)) {
        agents.forEach(agent => {
          agentCache.set(agent.agentId || agent.id, agent);
        });
      }

      // Initialize monitoring if not already done
      initAgentMonitoring();

      return response;
    } else {
      // Original behavior - use local cache
      if (agentCache.size === 0) {
        console.log('Agent cache is empty, initializing monitoring...');
        initAgentMonitoring();
      }

      return Array.from(agentCache.values());
    }
  } catch (error) {
    console.error('Error fetching agents:', error);

    // Fallback to local cache if backend fails
    console.log('Falling back to local agent cache');
    return Array.from(agentCache.values());
  }
};

/**
 * Fetch a single agent by ID
 */
export const fetchAgentById = async agentId => {
  try {
    if (isBackendEnabled) {
      // Get from backend
      const agent = await agentApi.fetchAgentById(agentId);

      // Update cache
      if (agent) {
        agentCache.set(agentId, agent);
      }

      return agent;
    } else {
      // Original behavior - use local cache
      return agentCache.get(agentId);
    }
  } catch (error) {
    console.error(`Error fetching agent ${agentId}:`, error);

    // Fallback to local cache
    return agentCache.get(agentId);
  }
};

/**
 * Create a new agent
 * Persists to backend and adds to local cache
 */
export const createAgent = async agent => {
  try {
    let createdAgent = agent;

    if (isBackendEnabled) {
      // Persist to backend
      createdAgent = await agentApi.adminCreateAgent(agent);
      toast.success(`Agent ${agent.agentName || agent.agentId} created successfully`);
    }

    // Update local cache (maintain original behavior)
    agentCache.set(createdAgent.agentId, {
      ...createdAgent,
      status: 'pending', // Agent hasn't connected yet
    });

    return createdAgent;
  } catch (error) {
    console.error('Error creating agent:', error);
    toast.error(`Failed to create agent: ${error.message || 'Unknown error'}`);
    throw error;
  }
};

/**
 * Update agent configuration
 * Persists to backend, then sends real-time update via MQTT
 */
export const updateAgent = async agent => {
  try {
    let updatedAgent = agent;

    if (isBackendEnabled) {
      // Persist to backend first
      updatedAgent = await agentApi.updateAgent(agent.agentId, agent);
      toast.success(`Agent ${agent.agentName || agent.agentId} updated`);

      // Persist full configuration (features + settings) to Firestore
      try {
        await agentApi.updateAgentConfig(agent.agentId, {
          features: agent.features || {},
          settings: agent.settings || {},
          // We'll publish MQTT ourselves below to avoid duplicate notifications
          publish_update: false,
        });
        console.log(
          `[AgentServiceBackend] Config persisted for agent ${agent.agentId} via /agent/config/update`
        );
      } catch (cfgErr) {
        console.error(
          `[AgentServiceBackend] Failed to persist config for agent ${agent.agentId}:`,
          cfgErr
        );
        toast.error(
          `Agent ${agent.agentName || agent.agentId}: failed to persist configuration. UI updated but backend settings may not be saved.`
        );
        // Continue to MQTT so the agent still receives the runtime update
      }
    }

    // Send real-time update to agent via MQTT
    await sendConfigToAgent(
      agent.agentId,
      agent.features || {},
      agent.settings || {}
    );

    // Update local cache with merged config so UI reflects changes immediately
    const mergedAgent = {
      ...updatedAgent,
      features: agent.features || updatedAgent.features || {},
      settings: agent.settings || updatedAgent.settings || {},
    };
    agentCache.set(agent.agentId, mergedAgent);

    return mergedAgent;
  } catch (error) {
    console.error('Error updating agent:', error);
    toast.error(`Failed to update agent: ${error.message || 'Unknown error'}`);
    throw error;
  }
};

/**
 * Delete an agent
 * Removes from backend and local cache
 */
export const deleteAgent = async agentId => {
  try {
    if (isBackendEnabled) {
      // Delete from backend
      await agentApi.deleteAgent(agentId);
      toast.success(`Agent ${agentId} deleted`);
    }

    // Remove from local cache
    agentCache.delete(agentId);

    return { success: true };
  } catch (error) {
    console.error('Error deleting agent:', error);
    toast.error(`Failed to delete agent: ${error.message || 'Unknown error'}`);
    throw error;
  }
};

/**
 * Toggle agent status (enable/disable)
 * Updates backend and sends command to agent via MQTT
 */
export const toggleAgentStatus = async (agentId, enabled) => {
  try {
    if (isBackendEnabled) {
      // Update in backend
      await agentApi.toggleAgentStatus(agentId, enabled);
    }

    // Send command to agent via MQTT
    await sendCommandToAgent(agentId, {
      type: enabled ? 'enable' : 'disable',
      timestamp: new Date().toISOString(),
    });

    // Update local cache
    if (agentCache.has(agentId)) {
      const agent = agentCache.get(agentId);
      agentCache.set(agentId, {
        ...agent,
        enabled,
      });
    }

    return { agentId, enabled };
  } catch (error) {
    console.error('Error toggling agent status:', error);
    toast.error(`Failed to update agent status: ${error.message || 'Unknown error'}`);
    throw error;
  }
};

/**
 * Restart an agent
 * Updates backend and sends restart command via MQTT
 */
export const restartAgent = async agentId => {
  try {
    if (isBackendEnabled) {
      // Update in backend
      await agentApi.restartAgent(agentId);
    }

    // Send restart command to agent via MQTT
    await sendCommandToAgent(agentId, {
      type: 'restart',
      timestamp: new Date().toISOString(),
    });

    return { success: true };
  } catch (error) {
    console.error('Error restarting agent:', error);
    toast.error(`Failed to restart agent: ${error.message || 'Unknown error'}`);
    throw error;
  }
};

/**
 * Fetch agent audit logs
 */
export const fetchAgentAuditLogs = async (agentId, params = {}) => {
  if (!isBackendEnabled) {
    return { data: [], meta: { total: 0 } };
  }

  try {
    const response = await agentApi.fetchAgentAuditLogs(agentId, params);

    // Handle v2 API response format
    if (response.logs) {
      // Legacy format
      return {
        data: response.logs,
        meta: response.pagination || { total: response.logs.length },
      };
    } else if (response.data && response.data.logs) {
      // v2 API format
      return {
        data: response.data.logs,
        meta: response.data.pagination || { total: response.data.logs.length },
      };
    }

    // Fallback
    return response;
  } catch (error) {
    console.error('Error fetching agent audit logs:', error);
    toast.error('Failed to load audit logs');
    throw error;
  }
};

/**
 * Initialize agent monitoring
 * This sets up listeners for agent status updates and populates the agent cache
 * Maintained from original implementation
 */
export const initAgentMonitoring = () => {
  console.log('Initializing agent monitoring with MQTT...');

  // Track heartbeat counts to reduce logging frequency
  const heartbeatCounts = {};

  // Handle agent status updates
  const handleAgentStatus = (agentId, statusData) => {
    // Only log status updates occasionally to reduce noise
    if (!heartbeatCounts[agentId]) {
      heartbeatCounts[agentId] = 0;
      console.log(`Received first status from agent ${agentId}:`, statusData);
    } else {
      heartbeatCounts[agentId]++;

      // Log only every 10th heartbeat
      if (heartbeatCounts[agentId] % 10 === 0) {
        console.log(`Received heartbeat #${heartbeatCounts[agentId]} from ${agentId}`);
      }
    }

    // Update agent in cache
    if (agentCache.has(agentId)) {
      const agent = agentCache.get(agentId);
      const updatedAgent = {
        ...agent,
        status: statusData.status || 'unknown',
        lastSeen: new Date().toISOString(),
      };

      // Extract fields from the status message
      if (statusData.agentName) updatedAgent.agentName = statusData.agentName;
      if (statusData.agentTenant) updatedAgent.agentTenant = statusData.agentTenant;
      if (statusData.agentSipId) updatedAgent.agentSipId = statusData.agentSipId;
      if (statusData.agentRestId) updatedAgent.agentRestId = statusData.agentRestId;

      // Update the cache
      agentCache.set(agentId, updatedAgent);
    } else {
      // Add as a new agent to cache (discovered via MQTT)
      agentCache.set(agentId, {
        agentId,
        status: statusData.status || 'unknown',
        lastSeen: new Date().toISOString(),
        agentName: statusData.agentName || agentId,
        agentTenant: statusData.agentTenant || '',
        agentSipId: statusData.agentSipId || '',
        agentRestId: statusData.agentRestId || '',
        _source: 'MQTT',
        ...statusData,
      });
    }
  };

  // Set up MQTT subscriptions for agent status updates
  const statusCleanup = subscribeToAgentStatus(handleAgentStatus);

  // Listen for all agents joining/leaving
  const agentsCleanup = listenForAllAgents((agentId, present) => {
    console.log(`Agent ${agentId} is ${present ? 'online' : 'offline'}`);

    if (agentCache.has(agentId)) {
      const agent = agentCache.get(agentId);
      agentCache.set(agentId, {
        ...agent,
        status: present ? 'online' : 'offline',
        lastSeen: present ? new Date().toISOString() : agent.lastSeen,
      });
    }
  });

  // Return a cleanup function to unsubscribe
  // (But we likely won't use it as we want monitoring to continue)
  return () => {
    statusCleanup();
    agentsCleanup();
  };
};

export const setBackendEnabled = enabled => {
  isBackendEnabled = enabled;
};

/**
 * Fetch agents with pagination and filtering
 * This is specifically used by the AgentManagementController component
 */
export const fetchAgentsWithPagination = async (params = {}) => {
  if (!isBackendEnabled) {
    // If backend is not enabled, use local cache with client-side pagination
    const allAgents = Array.from(agentCache.values());

    // Apply filters
    let filteredAgents = [...allAgents];

    if (params.search) {
      const searchLower = params.search.toLowerCase();
      filteredAgents = filteredAgents.filter(
        agent =>
          (agent.agentName && agent.agentName.toLowerCase().includes(searchLower)) ||
          (agent.agentId && agent.agentId.toLowerCase().includes(searchLower))
      );
    }

    if (params.status) {
      filteredAgents = filteredAgents.filter(agent => agent.status === params.status);
    }

    if (params.tenant_id) {
      filteredAgents = filteredAgents.filter(agent => agent.agentTenant === params.tenant_id);
    }

    // Apply pagination
    const page = parseInt(params.page, 10) || 1;
    const limit = parseInt(params.limit, 10) || 10;
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;

    const paginatedAgents = filteredAgents.slice(startIndex, endIndex);

    return {
      data: paginatedAgents,
      meta: {
        total: filteredAgents.length,
        page,
        limit,
        pages: Math.ceil(filteredAgents.length / limit),
      },
    };
  }

  try {
    // Use the v2 API endpoint
    const response = await agentApi.fetchAgents(params);

    // Handle v2 API response format
    if (response.data && response.data.items) {
      // v2 API format
      return {
        data: response.data.items,
        meta: {
          total: response.data.pagination?.total || 0,
          page: response.data.pagination?.page || 1,
          limit: response.data.pagination?.limit || 10,
          pages: response.data.pagination?.pages || 1,
        },
      };
    } else if (response.agents) {
      // Legacy format
      return {
        data: response.agents,
        meta: response.pagination || {
          total: response.agents.length,
          page: parseInt(params.page, 10) || 1,
          limit: parseInt(params.limit, 10) || 10,
        },
      };
    }

    // Fallback
    return response;
  } catch (error) {
    console.error('Error fetching agents with pagination:', error);

    // Fallback to local cache with client-side pagination
    return fetchAgentsWithPagination({ ...params, useBackend: false });
  }
};

// Export API with same interface
export default {
  fetchAgents,
  fetchAgentById,
  createAgent,
  updateAgent,
  deleteAgent,
  toggleAgentStatus,
  restartAgent,
  initAgentMonitoring,
  fetchAgentAuditLogs,
  fetchAgentsWithPagination,
  setBackendEnabled,
};
